import React, {useRef, useState} from 'react';
import {OneRepChartPoint} from 'components/dashboard/manager/nine-box/one-rep-chart/OneRepData.utils';
import {DashedLine} from 'components/dashboard/manager/nine-box/one-rep-chart/DashedLine';
import GoalTarget from './goal-target.svg';
import {GoalHoverPopup} from 'components/dashboard/manager/nine-box/one-rep-chart/GoalHoverPopup';
import {AvatarPoint} from 'charts/components/elements/AvatarPoint';
import {AvatarPointPopper} from '../avatar/AvatarPointPopper';
import {scales, svgDimensions} from 'components/dashboard/manager/nine-box/NineBox.const';
import clamp from 'lodash/clamp';
import {GraphGoal} from 'components/dashboard/manager/nine-box/NineBox.utils';

interface KpiGoalPointProps {
  xGoal: GraphGoal | undefined;
  yGoal: GraphGoal | undefined;
  latestPoint?: OneRepChartPoint;
}

const GOAL_COLOR = 'rgba(228, 95, 36, 0.3)';

const clampX = x => scales.xScale(clamp(x, 10, 90));
const clampY = x => scales.yScale(clamp(x, 10, 90));

/**
 * Renders the goal target, the line connecting it to the rep, and the crosshairs
 */
export const KpiGoalPoint: React.FC<KpiGoalPointProps> = ({xGoal, yGoal, latestPoint}) => {
  const anchorEl = useRef(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const onMouseEnter = () => setHoverOpen(true);
  const onMouseLeave = () => setHoverOpen(false);

  if (!xGoal && !yGoal) return null;
  const x = xGoal?.coordinate ?? latestPoint?.x ?? 50;
  const y = yGoal?.coordinate ?? latestPoint?.y ?? 50;

  return (
    <>
      {latestPoint && (
        <DashedLine
          at={[
            [latestPoint.x, latestPoint.y],
            [x, y],
          ]}
          color={GOAL_COLOR}
        />
      )}
      {yGoal && (
        <>
          <DashedLine
            at={[
              [0, y],
              [100, y],
            ]}
            color="#C4C4C4"
          />
          <text x={-15} y={clampY(y)} dy={-2} fontSize={8} textAnchor={'middle'}>
            Goal:
          </text>
          <text x={-15} y={clampY(y)} dy={8} fontSize={8} textAnchor={'middle'}>
            {yGoal.valueStr}
          </text>
        </>
      )}
      {xGoal && (
        <>
          <DashedLine
            at={[
              [x, 0],
              [x, 100],
            ]}
            color="#C4C4C4"
          />
          <text x={clampX(x)} y={svgDimensions.height + 10} textAnchor={'middle'} fontSize={8}>
            Goal: {xGoal.valueStr}
          </text>
        </>
      )}
      <AvatarPointPopper
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        open={hoverOpen}
        anchorEl={anchorEl}>
        <GoalHoverPopup xGoal={xGoal} yGoal={yGoal} />
      </AvatarPointPopper>
      <AvatarPoint
        coordinate={{x, y}}
        mainRef={anchorEl}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        image={GoalTarget}
      />
    </>
  );
};
